import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Newspaper, GraduationCap, ScrollText, FolderOpen, ExternalLink,
  Plus, Edit2, Trash2, Check, X, Link as LinkIcon, FileText, Download,
  Folder,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import { Button } from '../components/ui/button';
import FileDropZone from '../components/FileDropZone';
import EmptyState from '../components/EmptyState';
import { useReviewQueue } from '../contexts/ReviewQueueContext';
import { useT } from '../contexts/LocaleProvider';
import api from '../utils/api';

const fmtSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// The Lighting Guide is a static page shipped with the frontend; its resource
// row (created by migration 0013) renders as a featured card with a button.
const isGuide = (r) => !!(r.url && r.url.includes('/guides/lighting'));

const CATEGORY_DEFS = [
  { key: 'newsletters', icon: Newspaper     },
  { key: 'training',    icon: GraduationCap },
  { key: 'policies',    icon: ScrollText    },
  { key: 'other',       icon: FolderOpen    },
];

const emptyDraft = (category) => ({
  title: '', description: '', url: '', category, folder: '', icon: '', sort_order: 0,
  // file tri-state: undefined = no change; File = upload new; null = clear
});

const Resources = () => {
  const { count: reviewCount } = useReviewQueue();
  const { category: urlCategory } = useParams();
  const t = useT();
  const CATEGORIES = CATEGORY_DEFS.map(c => ({
    ...c,
    label: t(`resources.cat.${c.key}`),
    blurb: t(`resources.cat.${c.key}.blurb`),
  }));
  const catByKey = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));
  const activeCat = catByKey[urlCategory] || null;

  const [items, setItems] = useState(null);
  const [isHR, setIsHR] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft(activeCat ? activeCat.key : 'other'));
  const [error, setError] = useState('');

  useEffect(() => {
    api.resources().then(setItems).catch(() => setItems([]));
    // Same rule as the backend (IsHROrReadOnly): HR *or* app admins edit.
    Promise.all([api.isHR().catch(() => false), api.adminMe().then(d => !!d?.is_admin).catch(() => false)])
      .then(([hr, admin]) => setIsHR(hr || admin));
  }, []);

  // Deps: urlCategory only. activeCat is a fresh object each render and
  // would loop forever if included.
  useEffect(() => {
    setEditing(null);
    setError('');
    const valid = CATEGORY_DEFS.some(c => c.key === urlCategory);
    setDraft(emptyDraft(valid ? urlCategory : 'other'));
  }, [urlCategory]);

  const visibleCategories = activeCat ? [activeCat] : CATEGORIES;

  const grouped = useMemo(() => {
    const by = Object.fromEntries(CATEGORIES.map(c => [c.key, []]));
    for (const r of (items || [])) {
      (by[r.category] || by.other).push(r);
    }
    return by;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const startNew = (category) => {
    setEditing(`new:${category}`);
    setDraft(emptyDraft(category));
    setError('');
  };
  const startEdit = (r) => {
    setEditing(r.id);
    setDraft({
      title: r.title, description: r.description || '', url: r.url || '',
      category: r.category, folder: r.folder || '',
      icon: r.icon || '', sort_order: r.sort_order || 0,
      _existing_file: r.file || null,
      _existing_original_name: r.original_name || '',
      _existing_size: r.size || 0,
    });
    setError('');
  };
  const cancel = () => { setEditing(null); setError(''); };

  const save = async () => {
    if (!draft.title.trim()) { setError(t('resources.editor.titleRequired')); return; }
    // Strip our local-only fields from the payload before sending.
    const { _existing_file, _existing_original_name, _existing_size, ...payload } = draft;
    void _existing_file; void _existing_original_name; void _existing_size;
    try {
      if (String(editing).startsWith('new:')) {
        const created = await api.createResource(payload);
        setItems(arr => [...arr, created]);
      } else {
        const updated = await api.updateResource(editing, payload);
        setItems(arr => arr.map(x => x.id === editing ? updated : x));
      }
      setEditing(null);
    } catch (e) {
      setError(e.response?.data?.detail || t('editor.saveFailed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('resources.confirmDelete'))) return;
    try {
      await api.deleteResource(id);
      setItems(arr => arr.filter(x => x.id !== id));
    } catch {
      setError(t('editor.saveFailed'));
    }
  };

  return (
    <Shell reviewCount={reviewCount}>
      <header className="mb-6">
        <h1 className="font-display text-[1.75rem]">
          {activeCat ? activeCat.label : t('resources.title')}
        </h1>
        <p className="text-[15px] text-[hsl(var(--text-soft))] mt-1.5">
          {activeCat ? activeCat.blurb : t('resources.intro')}
          {isHR && <span> {t('resources.hrCanEdit')}</span>}
        </p>
      </header>

      {error && (
        <div className="mb-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {visibleCategories.map(cat => (
          <CategorySection
            key={cat.key}
            cat={cat}
            items={grouped[cat.key] || []}
            isHR={isHR}
            editingId={editing}
            draft={draft}
            setDraft={setDraft}
            onStartNew={() => startNew(cat.key)}
            onStartEdit={startEdit}
            onSave={save}
            onCancel={cancel}
            onDelete={remove}
            standalone={!!activeCat}
            t={t}
          />
        ))}
      </div>
    </Shell>
  );
};

const CategorySection = ({ cat, items, isHR, editingId, draft, setDraft, onStartNew, onStartEdit, onSave, onCancel, onDelete, standalone, t }) => {
  const Icon = cat.icon;
  const newKey = `new:${cat.key}`;
  const composing = editingId === newKey;

  // Sub-group by folder, preserving the order the backend gave us.
  // Empty folder = loose items, rendered first with no header.
  const folderGroups = useMemo(() => {
    const groups = new Map();
    groups.set('', []);
    for (const r of items) {
      const f = r.folder || '';
      if (!groups.has(f)) groups.set(f, []);
      groups.get(f).push(r);
    }
    if (groups.get('').length === 0) groups.delete('');
    return Array.from(groups.entries());
  }, [items]);

  // Distinct folder names in this category — feeds the editor's datalist.
  const knownFolders = useMemo(
    () => Array.from(new Set(items.map(r => r.folder).filter(Boolean))),
    [items],
  );

  return (
    <section className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
      {!standalone && (
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center h-9 w-9 rounded-md bg-[hsl(var(--bg-subtle))] border border-border">
              <Icon className="h-4 w-4 text-[hsl(var(--text-soft))]" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display text-[1.15rem] leading-tight">{cat.label}</h2>
              <div className="text-[13px] text-[hsl(var(--text-muted))]">{cat.blurb}</div>
            </div>
          </div>
          {isHR && !composing && (
            <Button variant="outline" size="sm" onClick={onStartNew} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" /> {t('common.add')}
            </Button>
          )}
        </div>
      )}

      {standalone && isHR && !composing && (
        <div className="px-5 py-3 border-b border-border flex items-center justify-end">
          <Button size="sm" onClick={onStartNew} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> {t('common.add')}
          </Button>
        </div>
      )}

      {items.length === 0 && !composing && (
        <div className="px-5">
          <EmptyState compact title={t('resources.empty')} />
        </div>
      )}

      <ul className="divide-y divide-[hsl(var(--rule))]">
        {composing && (
          <li className="px-5 py-4 bg-[hsl(var(--bg-subtle))]">
            <EditorRow draft={draft} setDraft={setDraft} onSave={onSave} onCancel={onCancel} knownFolders={knownFolders} t={t} />
          </li>
        )}

        {folderGroups.map(([folderName, folderItems]) => (
          <React.Fragment key={folderName === '' ? '__loose__' : `f:${folderName}`}>
            {folderName && (
              <li className="px-5 pt-4 pb-1.5 text-[11px] uppercase tracking-wider text-[hsl(var(--text-muted))] flex items-center gap-1.5">
                <Folder className="h-3 w-3" />
                {folderName}
              </li>
            )}
            {folderItems.map(r => editingId === r.id ? (
              <li key={r.id} className="px-5 py-4 bg-[hsl(var(--bg-subtle))]">
                <EditorRow draft={draft} setDraft={setDraft} onSave={onSave} onCancel={onCancel} knownFolders={knownFolders} t={t} />
              </li>
            ) : (
              <li key={r.id} className={isGuide(r) ? 'px-5 py-4 bg-[hsl(var(--accent-soft))]' : 'px-5 py-3 hover:bg-[hsl(var(--surface-2))]'}>
                <div className="flex items-center gap-3">
                  <div className="text-[20px] w-7 text-center shrink-0">{r.icon || '•'}</div>
                  <div className="flex-1 min-w-0">
                    <ResourceLink r={r} t={t} />
                    {r.description && <div className={`text-[13px] text-[hsl(var(--text-soft))] mt-0.5 ${isGuide(r) ? '' : 'truncate'}`}>{r.description}</div>}
                  </div>
                  {isGuide(r) && (
                    <Button asChild size="sm" className="gap-1.5 shrink-0">
                      <a href={r.url} target="_blank" rel="noreferrer">
                        {t('resources.openGuide')} <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                  )}
                  {isHR && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => onStartEdit(r)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]" aria-label="Modifier">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => onDelete(r.id)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label="Supprimer">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </React.Fragment>
        ))}
      </ul>
    </section>
  );
};

const ResourceLink = ({ r, t }) => {
  // Prefer the uploaded file when present (it's the most authoritative source).
  // Fall back to the external URL, and surface a warning pill if neither is set.
  if (r.file) {
    return (
      <a href={r.file} target="_blank" rel="noreferrer"
         className="text-[15px] hover:text-[hsl(var(--accent))] inline-flex items-center gap-1.5">
        <span className="truncate">{r.title}</span>
        <FileText className="h-3.5 w-3.5 shrink-0 opacity-60" />
        {r.size > 0 && <span className="text-[11px] text-[hsl(var(--text-muted))] font-num">{fmtSize(r.size)}</span>}
      </a>
    );
  }
  if (r.url) {
    return (
      <a href={r.url} target="_blank" rel="noreferrer"
         className="text-[15px] hover:text-[hsl(var(--accent))] inline-flex items-center gap-1.5">
        <span className="truncate">{r.title}</span>
        <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" />
      </a>
    );
  }
  return (
    <div className="text-[15px] flex items-center gap-2">
      <span className="truncate">{r.title}</span>
      <span className="text-[11px] text-[hsl(var(--warning))] border border-[hsl(var(--warning)/.4)] bg-[hsl(var(--warning-soft))] rounded-full px-2 py-0.5">
        {t('resources.toComplete')}
      </span>
    </div>
  );
};

const EditorRow = ({ draft, setDraft, onSave, onCancel, knownFolders = [], t }) => {
  const folderListId = `folders-${draft.category}`;
  // What's currently on the server for this resource (only present in edit mode)
  const existingFileUrl = draft._existing_file || null;
  const existingFileName = draft._existing_original_name || (existingFileUrl ? existingFileUrl.split('/').pop() : '');
  const existingFileSize = draft._existing_size || 0;
  // Local state for the file field
  const stagedFile = draft.file instanceof File ? draft.file : null;
  const willClear = draft.file === null;

  const dropFile = (files) => {
    if (files && files.length) setDraft({ ...draft, file: files[0] });
  };
  const clearStaged = () => {
    const { file: _, ...rest } = draft;  // eslint-disable-line no-unused-vars
    setDraft(rest);
  };
  const removeExisting = () => setDraft({ ...draft, file: null });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-3">
        <div className="field">
          <label className="field-label">{t('resources.editor.icon')}</label>
          <input className="input text-center text-[18px]" maxLength={4} placeholder="📄"
            value={draft.icon} onChange={e => setDraft({ ...draft, icon: e.target.value })} />
        </div>
        <div className="field">
          <label className="field-label">{t('resources.editor.title')}</label>
          <input className="input" value={draft.title}
            onChange={e => setDraft({ ...draft, title: e.target.value })}
            placeholder={t('resources.editor.title.ph')} />
        </div>
      </div>

      <p className="text-[12px] text-[hsl(var(--text-muted))] italic">{t('resources.editor.either')}</p>

      <div className="field">
        <label className="field-label">{t('resources.editor.url')}</label>
        <div className="relative">
          <LinkIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input className="input !pl-9 font-num text-[13.5px]" type="url" value={draft.url}
            onChange={e => setDraft({ ...draft, url: e.target.value })}
            placeholder="https://www.notion.so/…" />
        </div>
      </div>

      <div className="field">
        <label className="field-label">{t('resources.editor.file')}</label>
        {/* Existing file (only when editing and not flagged for removal) */}
        {existingFileUrl && !willClear && !stagedFile && (
          <div className="flex items-center justify-between px-3 py-2 border border-border rounded-md bg-[hsl(var(--bg-subtle))] text-[14px] mb-2">
            <a href={existingFileUrl} target="_blank" rel="noreferrer"
               className="flex items-center gap-2 text-[hsl(var(--accent))] hover:underline truncate">
              <Download className="h-4 w-4 shrink-0" />
              <span className="truncate">{existingFileName}</span>
              {existingFileSize > 0 && <span className="text-[12px] text-[hsl(var(--text-muted))] font-num">{fmtSize(existingFileSize)}</span>}
            </a>
            <button type="button" onClick={removeExisting} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label={t('resources.editor.removeFile')}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {/* Staged file (newly added in this edit session) */}
        {stagedFile && (
          <div className="flex items-center justify-between px-3 py-2 border border-border rounded-md bg-[hsl(var(--bg-subtle))] text-[14px] mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0 text-[hsl(var(--text-soft))]" />
              <span className="truncate">{stagedFile.name}</span>
              <span className="text-[12px] text-[hsl(var(--text-muted))] font-num">{fmtSize(stagedFile.size)}</span>
            </div>
            <button type="button" onClick={clearStaged} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label={t('common.cancel')}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <FileDropZone
          onFiles={dropFile}
          multiple={false}
          compact
          label={t('resources.editor.file.drop')}
          sublabel={t('resources.editor.file.sub')}
        />
      </div>

      <div className="field">
        <label className="field-label">
          {t('resources.editor.desc')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span>
        </label>
        <input className="input" value={draft.description}
          onChange={e => setDraft({ ...draft, description: e.target.value })}
          placeholder={t('resources.editor.desc.ph')} />
      </div>
      <div className="field">
        <label className="field-label">
          {t('resources.editor.folder')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span>
        </label>
        <div className="relative">
          <Folder className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            className="input !pl-9"
            value={draft.folder}
            onChange={e => setDraft({ ...draft, folder: e.target.value })}
            placeholder={t('resources.editor.folder.ph')}
            list={folderListId}
            maxLength={80}
          />
          {knownFolders.length > 0 && (
            <datalist id={folderListId}>
              {knownFolders.map(f => <option key={f} value={f} />)}
            </datalist>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="field">
          <label className="field-label">{t('resources.editor.category')}</label>
          <select value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })}>
            {CATEGORY_DEFS.map(c => <option key={c.key} value={c.key}>{t(`resources.cat.${c.key}`)}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field-label">{t('resources.editor.order')}</label>
          <input className="input" type="number" value={draft.sort_order}
            onChange={e => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} className="gap-1.5">
          <X className="h-3.5 w-3.5" /> {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={onSave} className="gap-1.5">
          <Check className="h-3.5 w-3.5" /> {t('common.save')}
        </Button>
      </div>
    </div>
  );
};

export default Resources;
